/* eslint-disable no-use-before-define */
import { Directive, ElementRef, HostListener, Renderer2, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { formatCurrency } from '../../../helpers/format-helpers';
import { FormsValidators } from '../validators';

enum KeyCodes {
  Tab = 9,
  Shift = 16,
  Space = 32,
  LeftArrow = 37,
  UpArrow = 38,
  RightArrow = 39,
  DownArrow = 40,
  Insert = 45,
  Delete = 46,
  Zero = 48,
  Nine = 57,
  ZeroNumpad = 96,
  NineNumpad = 105,
}

// eslint-disable-next-line no-empty-function
const noop = () => {};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CurrencyMaskDirective),
  multi: true,
};

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[currencyMask]',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  private readonly el: HTMLInputElement;
  private modelValue: any;

  public constructor() {
    const elementRef = this.elementRef;

    this.el = elementRef.nativeElement;
  }

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (a: any) => void = noop;

  @HostListener('focus', ['$event.target.value'])
  public onfocus(value) {
    const valid = FormsValidators.greaterThanZero(value);
    if (!valid) {
      this.el.value = '';
    }
  }

  @HostListener('blur', ['$event.target.value'])
  public onBlur(value) {
    this.onTouchedCallback();
    const valid = FormsValidators.greaterThanZero(value);
    if (!valid) {
      this.el.value = '0';
      this.modelValue = 0;
    } else {
      this.el.value = this.format(value);
      this.modelValue = this.parse(this.el.value);
      this.onChangeCallback(this.modelValue);
      if (this.modelValue) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
      }
    }
  }

  @HostListener('change', ['$event.target.value'])
  public onChange(value) {
    this.el.value = this.format(value);
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event) {
    this.preventInvalidEntry(event);
  }

  @HostListener('keyup', ['$event'])
  public onKeyUp(event) {
    this.preventInvalidEntry(event);

    const key = event.which ?? event.keyCode ?? 0;

    let value = event.target.value.replace(/,/g, '');

    if (key === KeyCodes.UpArrow) {
      value = +value + 1;
    } else if (key === KeyCodes.DownArrow) {
      if (+value > 0) {
        value = +value - 1;
      }
    }

    this.el.value = this.format(value.toString());
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  public get value(): any {
    return this.modelValue;
  }
  public set value(v: any) {
    if (v !== this.modelValue) {
      this.modelValue = v;
      this.onChangeCallback(v);
    }
  }

  public writeValue(value: any) {
    if (value !== this.modelValue) {
      this.el.value = this.format(value);
      if (value) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'value', value);
      }
      this.modelValue = value;
    }
  }

  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // to display value
  private format(value: string) {
    return formatCurrency(value);
  }

  // to model
  private parse(value: string) {
    value = value.replace(/[^\d.]/g, '');
    return value;
  }

  private preventInvalidEntry(event) {
    const key = event.which ?? event.keyCode ?? 0;

    if (
      key === KeyCodes.Insert ||
      (key !== KeyCodes.Delete &&
        key >= KeyCodes.Space &&
        !(
          (key >= KeyCodes.Zero && key <= KeyCodes.Nine) ||
          (key >= KeyCodes.ZeroNumpad && key <= KeyCodes.NineNumpad) ||
          key === KeyCodes.LeftArrow ||
          key === KeyCodes.RightArrow
        ))
    ) {
      event.preventDefault();
    }
  }
}
